/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { paragraphMedium, paragraphSmall, weightBold } from 'src/components/css'

interface EmptyStateBoxProps {
  /** Icon rendered inside the circular badge on top. */
  icon: React.ReactNode
  /** Title (rendered in Title Case). */
  title: string
  /** Supporting line beneath the title. */
  subtitle?: string
  /** Optional call-to-action rendered below the text (e.g. a button). */
  action?: React.ReactNode
}

/**
 * Centered, full-width boxed empty state: a circular icon badge, a title, an
 * optional subtitle, and an optional action. Matches the card/form box border.
 */
const EmptyStateBox: React.FC<EmptyStateBoxProps> = ({
  icon,
  title,
  subtitle,
  action,
}) => (
  <div css={wrapStyle}>
    <div css={boxStyle}>
      <div css={iconBadgeStyle}>{icon}</div>
      <p css={titleStyle}>{capitalizeWords(title)}</p>
      {subtitle ? <p css={textStyle}>{subtitle}</p> : null}
      {action ? <div css={actionStyle}>{action}</div> : null}
    </div>
  </div>
)

export default EmptyStateBox

const wrapStyle = css`
  display: flex;
  justify-content: center;
  width: 100%;
`
const boxStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 40px 24px;
  width: 100%;
  background-color: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 12px;
`
const iconBadgeStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${colors.SECONDARY_300};
  color: ${colors.NEUTRAL_50};
  font-size: 24px;
  margin-bottom: 4px;
`
const titleStyle = css`
  ${paragraphMedium};
  ${weightBold};
  color: ${colors.WHITE};
  margin: 0;
`
const textStyle = css`
  ${paragraphSmall};
  color: ${colors.NEUTRAL_50};
  margin: 0;
  line-height: 1.5;
`
const actionStyle = css`
  margin-top: 12px;
`
